import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useOrganizationSelector } from '@/hooks/use-organization-selector';
import { getInitials } from '@/lib/utils';
import { apiRequest } from '@/lib/queryClient';

interface OrganizationAvatarProps {
  /** Size of the avatar in pixels. Defaults to 40px (w-10 h-10) */
  size?: 'sm' | 'md' | 'lg';
  /** Custom className for the container */
  className?: string;
}

const sizeClasses = {
  sm: 'w-6 h-6 text-xs',
  md: 'w-10 h-10 text-xs',
  lg: 'w-12 h-12 text-sm',
};

/**
 * Organization avatar component
 * - Displays organization logo or initials
 * - When clicked, shows organization selector dropdown
 * - For contractors: Shows dropdown to select between organizations
 * - For employees: Shows read-only display but still clickable
 */
export function OrganizationAvatar({ size = 'md', className = '' }: OrganizationAvatarProps) {
  const { t } = useTranslation();
  const {
    activeOrganizationId,
    activeOrganizationName,
    selectedOrgId,
    setSelectedOrgIdAndReset,
    organizations,
    canSelect,
  } = useOrganizationSelector();

  // Track if image failed to load
  const [imageError, setImageError] = useState(false);
  const prevOrgIdRef = useRef<string | null>(null);
  // Track popover open state (must be declared before any conditional returns)
  const [open, setOpen] = useState(false);

  // Fetch organization logo for the active organization
  const { data: orgMeResponse } = useQuery({
    queryKey: ["/api/orgs/me", activeOrganizationId],
    queryFn: async () => {
      const url = activeOrganizationId 
        ? `/api/orgs/me?organizationId=${encodeURIComponent(activeOrganizationId)}`
        : '/api/orgs/me';
      const res = await apiRequest(url);
      return await res.json();
    },
    staleTime: 60_000,
    retry: false,
    enabled: !!activeOrganizationId,
  });
  const orgInfo = (orgMeResponse as any)?.data || null;
  const orgLogoUrl = orgInfo?.logoUrl || null;

  // Reset image error when organization changes (only if org actually changed)
  useEffect(() => {
    if (prevOrgIdRef.current !== activeOrganizationId) {
      prevOrgIdRef.current = activeOrganizationId;
      setImageError(false);
    }
  }, [activeOrganizationId]);

  const sizeClass = sizeClasses[size];
  const orgInitials = activeOrganizationName ? getInitials(activeOrganizationName) : 'O';

  // Determine if we should show logo or initials
  const shouldShowLogo = orgLogoUrl && !imageError && orgLogoUrl.trim() !== '';

  // Handle image error - use callback to prevent infinite loops
  const handleImageError = React.useCallback(() => {
    setImageError((prev) => {
      // Only update if not already set to true
      if (!prev) {
        return true;
      }
      return prev;
    });
  }, []);

  // If no organization, don't render
  if (!activeOrganizationName) {
    return null;
  }

  // For employees or when no selection is possible, show read-only but still clickable avatar
  // Even if canSelect is false, show the popover if there are organizations (for future expansion)
  if (!canSelect && organizations.length <= 1) {
    return (
      <div className={`${sizeClass} rounded-full overflow-hidden avatar-border bg-[var(--primary-blue)] flex items-center justify-center cursor-pointer ${className}`}>
        {shouldShowLogo ? (
          <img
            key={`org-logo-${activeOrganizationId}`}
            src={orgLogoUrl}
            alt={activeOrganizationName || 'Organization logo'}
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
        ) : (
          <span className="text-white font-medium">
            {orgInitials}
          </span>
        )}
      </div>
    );
  }

  // For contractors with multiple organizations: show selectable dropdown with avatar trigger
  // Only render Popover if we have organizations to avoid ref issues
  if (organizations.length === 0) {
    return (
      <div className={`${sizeClass} rounded-full overflow-hidden avatar-border bg-[var(--primary-blue)] flex items-center justify-center cursor-pointer ${className}`}>
        {shouldShowLogo ? (
          <img
            key={`org-logo-${activeOrganizationId}`}
            src={orgLogoUrl}
            alt={activeOrganizationName || 'Organization logo'}
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
        ) : (
          <span className="text-white font-medium">
            {orgInitials}
          </span>
        )}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(!open);
          }}
          className={`${sizeClass} rounded-full overflow-hidden avatar-border bg-[var(--primary-blue)] flex items-center justify-center cursor-pointer hover:opacity-90 ${className}`}
          aria-label={activeOrganizationName || 'Select Organization'}
        >
          {shouldShowLogo ? (
            <img
              key={`org-logo-${activeOrganizationId}`}
              src={orgLogoUrl}
              alt={activeOrganizationName || 'Organization logo'}
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
          ) : (
            <span className="text-white font-medium">
              {orgInitials}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-56 p-1 z-[100]" 
        align="end" 
        side="bottom" 
        sideOffset={8}
      >
        <div className="space-y-1">
          {organizations.map((org: any) => (
            <button
              key={org.id}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedOrgIdAndReset(org.id);
                setOpen(false);
              }}
              className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground ${
                selectedOrgId === org.id ? 'bg-accent font-medium' : ''
              }`}
            >
              {org.name}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

